import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiHome } from 'react-icons/fi';
import { instituteAPI } from '../../services/api';
import InstituteModal from './InstituteModal';

const Institutes = () => {
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchInstitutes();
  }, []);

  const fetchInstitutes = async () => {
    try {
      setLoading(true);
      const response = await instituteAPI.getAll();
      setInstitutes(response.data || []);
    } catch (error) {
      console.error('Error fetching institutes:', error);
      setError('Failed to load institutes');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedInstitute(null);
    setShowModal(true);
  };

  const handleEdit = (institute) => {
    setSelectedInstitute(institute);
    setShowModal(true);
  };

  const handleDelete = async (uuid) => {
    if (!window.confirm('Are you sure you want to delete this institute? This action cannot be undone.')) {
      return;
    }

    try {
      await instituteAPI.delete(uuid);
      fetchInstitutes();
    } catch (error) {
      console.error('Error deleting institute:', error);
      alert('Failed to delete institute');
    }
  };

  const handleSave = async (instituteData) => {
    try {
      if (selectedInstitute) {
        // Update existing
        await instituteAPI.update(selectedInstitute.uuid, instituteData);
      } else {
        // Create new
        await instituteAPI.create(instituteData);
      }
      setShowModal(false);
      fetchInstitutes();
    } catch (error) {
      console.error('Error saving institute:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Institute Management</h1>
            <p className="text-gray-600">Configure and manage institutes in the system</p>
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <FiPlus className="h-5 w-5 mr-2" />
            Add Institute
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            {error}
          </div>
        )}

        {/* Institutes Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {institutes.map((institute) => (
            <div
              key={institute.uuid}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  {institute.logoUrl ? (
                    <img
                      src={institute.logoUrl}
                      alt={institute.name}
                      className="h-12 w-12 rounded object-cover mr-3"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded bg-gray-200 flex items-center justify-center mr-3">
                      <FiHome className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{institute.name}</h3>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        institute.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {institute.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                {institute.address && (
                  <p className="text-sm text-gray-600">{institute.address}</p>
                )}
                <div className="bg-blue-50 border border-blue-200 rounded px-3 py-2 mb-2">
                  <p className="text-xs text-blue-600 font-semibold mb-1">Survey URL:</p>
                  <p className="text-sm font-mono text-blue-800">/survey?district={institute.shortcode}</p>
                </div>
                <div className="text-xs text-gray-500">
                  <p className="font-mono">UUID: {institute.uuid}</p>
                  <p className="mt-1">Students: {institute.collections.students}</p>
                  <p>Roster: {institute.collections.roster}</p>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-2 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleEdit(institute)}
                  className="flex items-center px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                >
                  <FiEdit2 className="h-4 w-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(institute.uuid)}
                  className="flex items-center px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                  <FiTrash2 className="h-4 w-4 mr-1" />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {institutes.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <FiHome className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No institutes configured yet</p>
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <FiPlus className="h-5 w-5 mr-2" />
              Add Your First Institute
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <InstituteModal
          institute={selectedInstitute}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default Institutes;
